import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Spinner, Alert, Badge, Form, InputGroup, Button, Pagination } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import inboundApi from '../../config/inboundApi';

const STATUS_OPTIONS = ['', 'new', 'open', 'pending', 'solved', 'closed'];

const STATUS_COLORS = {
  new: 'primary',
  open: 'warning',
  pending: 'info',
  solved: 'success',
  closed: 'secondary',
};

export default function TicketsTab() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const fetchTickets = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await inboundApi.get('/tickets', { params });
      setTickets(res.data.tickets);
      setTotal(res.data.total);
    } catch (err) {
      setError('Failed to load tickets');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [search, statusFilter, page]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => { fetchTickets(true); }, 15000);
    return () => clearInterval(interval);
  }, [fetchTickets]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
        <InputGroup style={{ maxWidth: 300 }}>
          <Form.Control
            placeholder="Search tickets..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </InputGroup>
        <Form.Select
          style={{ maxWidth: 180 }}
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.filter(Boolean).map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </Form.Select>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner /></div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No tickets found.
            </div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Subject</th>
                  <th>Requester</th>
                  <th>Status</th>
                  <th>Messages</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => (
                  <tr
                    key={t.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/tickets/${t.id}`)}
                  >
                    <td><strong>{t.ticket_number}</strong></td>
                    <td>{t.subject}</td>
                    <td>
                      <div>{t.requester?.name || '-'}</div>
                      <small className="text-muted">{t.requester?.email}</small>
                    </td>
                    <td>
                      <Badge bg={STATUS_COLORS[t.status] || 'secondary'}>
                        {t.status}
                      </Badge>
                    </td>
                    <td>{t.message_count}</td>
                    <td>
                      <small className="text-muted">
                        {new Date(t.created_at).toLocaleDateString()}
                      </small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
          </small>
          <Pagination className="mb-0">
            <Pagination.Prev disabled={page <= 1} onClick={() => setPage(page - 1)} />
            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
              const p = i + 1;
              return (
                <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>
                  {p}
                </Pagination.Item>
              );
            })}
            {totalPages > 5 && <Pagination.Ellipsis disabled />}
            <Pagination.Next disabled={page >= totalPages} onClick={() => setPage(page + 1)} />
          </Pagination>
        </div>
      )}
    </div>
  );
}
